import { Box, Typography, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import SupplierFilterSelect from "components/list/SupplierFilterSelect";
import FixedSizeDialog from "components/fixedSizeDialog";
import PurchaseOrderViewForm from "../forms/purchaseOrderForms/purchaseOrderViewForm";
import { usePurchaseOrderList, useSupplierList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function PurchaseOrders({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const bodyBg = theme.palette.background.alt;
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    purchaseOrderList, count, isLoadingPurchaseOrderList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
    setSearchPurchaseOrder, setSupplierFilter,
  } = usePurchaseOrderList(1, business_branch_id);
  const { supplierList } = useSupplierList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState({});

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/purchase_order/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickEditOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_order/get/${id}`);
    if (res.status === 200) navigate("/editPurchaseOrderForm", { state: res.data.data });
  };

  const handleClickDelete = async (id) => {
    const res = await requester.get(`/inventory/purchase_order/get/${id}`);
    if (res.status === 200) setIsSuccess(true);
  };

  const columns = [
    { label: "Order No."               },
    { label: "Date Created"            },
    { label: "Supplier"                },
    { label: "Expected Delivery"       },
    { label: "Actions", align: "center" },
  ];

  const ActionBtn = ({ label, icon, color, borderColor, hoverBg, onClick }) => (
    <Box onClick={onClick} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: `1px solid ${borderColor}`, cursor: "pointer", color, fontSize: "12px", fontWeight: 500, transition: "all 0.15s", "&:hover": { backgroundColor: hoverBg } }}>
      <Box sx={{ fontSize: "14px", display: "flex" }}>{icon}</Box> {label}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader icon={<ShoppingCartIcon sx={{ fontSize: "22px" }} />} title="Purchase Orders" count={count}>
        <Button onClick={() => navigate("/purchaseOrderForm")} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Order
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
        <ListSearchField placeholder="Search order number…" onSearch={setSearchPurchaseOrder} />
        <SupplierFilterSelect suppliers={supplierList} onChange={setSupplierFilter} />
      </ListFilterBar>

      <ReportTable columns={columns} rows={purchaseOrderList} loading={isLoadingPurchaseOrderList}
        hasMore={hasMore} onLoadMore={loadMore} loadingMore={isLoadingMore}
        emptyMessage="No purchase orders found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.id}</Typography></RCell>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell>{row.related_supplier.name}</RCell>
            <RCell>{moment(row.delivery_date).format("LL")}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <ActionBtn label="View" icon={<VisibilityIcon fontSize="inherit" />} color={accent} borderColor="rgba(29,158,117,0.3)" hoverBg="rgba(29,158,117,0.08)" onClick={() => handleClickView(row.id)} />
                <ActionBtn label="Edit" icon={<EditOutlinedIcon fontSize="inherit" />} color="#BA7517" borderColor="rgba(186,117,23,0.3)" hoverBg="rgba(186,117,23,0.08)" onClick={() => handleClickEditOpen(row.id)} />
                <ActionBtn label="Delete" icon={<DeleteOutlineIcon fontSize="inherit" />} color="#e24b4a" borderColor="rgba(226,75,74,0.3)" hoverBg="rgba(226,75,74,0.08)" onClick={() => handleClickDelete(row.id)} />
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Purchase Order Details" negativeResponse="Close" maxWidth="lg" scroll="body"
        content={<PurchaseOrderViewForm viewData={viewData} />}
      />
    </Box>
  );
}